import { useLocale } from './useLocale';

/**
 * Composable for regional formatting (Date, Time, Currency) with i18n locale support
 * Standardizes the display of regional configurations
 */
export function useRegionalFormat() {
  const { resolvedLocale } = useLocale();

  /**
   * Formats a date with short month abbreviation, day, and year (e.g., 'May 10, 2026' or '10 de mai. de 2026')
   * Matches the 'MMM d, yyyy' style but properly localized.
   *
   * @param {string|number|Date} date - The date to format
   * @returns {string} Formatted date string
   */
  const formatAbbreviatedDate = date => {
    if (!date) return '';
    const parts = new Intl.DateTimeFormat(resolvedLocale.value, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).formatToParts(new Date(date));

    const month = parts.find(p => p.type === 'month')?.value || '';
    const day = parts.find(p => p.type === 'day')?.value || '';
    const year = parts.find(p => p.type === 'year')?.value || '';

    // Capitalize month and remove any trailing dots that some locales might add (optional, but keeps it clean)
    const cleanMonth = month.replace(/\.$/, '');
    const capMonth = cleanMonth.charAt(0).toUpperCase() + cleanMonth.slice(1);

    return `${capMonth} ${day}, ${year}`;
  };

  /**
   * Formats a weekday name in the current locale
   *
   * @param {string|number|Date} date - The date to format
   * @param {string} style - 'long' (e.g., Monday), 'short' (e.g., Mon), or 'narrow' (e.g., M)
   * @returns {string} Localized weekday name
   */
  const formatWeekday = (date, style = 'short') => {
    if (!date) return '';
    const name = new Intl.DateTimeFormat(resolvedLocale.value, {
      weekday: style,
    }).format(new Date(date));

    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  /**
   * Formats a time string (e.g., '14:30' or '2:30 PM' depending on locale)
   *
   * @param {string|number|Date} date - The date/time to format
   * @returns {string} Localized time string
   */
  const formatTime = date => {
    if (!date) return '';
    return new Intl.DateTimeFormat(resolvedLocale.value, {
      hour: 'numeric',
      minute: 'numeric',
    }).format(new Date(date));
  };

  /**
   * Formats a month name in the current locale
   *
   * @param {string|number|Date} date - The date to format
   * @param {string} style - 'long' (e.g., January), 'short' (e.g., Jan), or 'narrow' (e.g., J)
   * @returns {string} Localized month name
   */
  const formatMonth = (date, style = 'long') => {
    if (!date) return '';
    const name = new Intl.DateTimeFormat(resolvedLocale.value, {
      month: style,
    }).format(new Date(date));

    return name.charAt(0).toUpperCase() + name.slice(1);
  };

  /**
   * Formats a year in the current locale
   *
   * @param {string|number|Date} date - The date to format
   * @returns {string} Localized year
   */
  const formatYear = date => {
    if (!date) return '';
    return new Intl.DateTimeFormat(resolvedLocale.value, {
      year: 'numeric',
    }).format(new Date(date));
  };

  /**
   * Formats a currency value based on the current locale

   *
   * @param {number} value - The numeric value to format
   * @param {string} currency - The ISO 4217 currency code (e.g., 'USD', 'BRL', 'EUR')
   * @returns {string} Formatted currency string
   */
  const formatCurrency = (value, currency = 'USD') => {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      return '0';
    }
    return new Intl.NumberFormat(resolvedLocale.value, {
      style: 'currency',
      currency,
    }).format(value);
  };

  return {
    formatAbbreviatedDate,
    formatWeekday,
    formatTime,
    formatCurrency,
    formatMonth,
    formatYear,
  };
}
